import GoogleIcon from '@mui/icons-material/Google';
import WindowIcon from '@mui/icons-material/Window';
import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSiteContext } from "../../state/SiteContext";
import { useAuth } from "./AuthProvider";
// Note: CSS is now loaded through theme system - see theme-styles/login-modal-template.css

interface LoginProps {
    open: boolean;
    onClose: () => void;
}

export default function Login({ open, onClose }: LoginProps) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [emailError, setEmailError] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");
    const { siteState } = useSiteContext();
    const { login, loginWithProvider, error, isLoading, loading, showForgotPassword } = useAuth();
    const authInfo = siteState.site?.auth;

    const validateEmail = (emailValue: string): boolean => {
        if (!emailValue.trim()) {
            setEmailError("Email is required");
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) {
            setEmailError("Please enter a valid email address");
            return false;
        }
        setEmailError("");
        return true;
    };

    const validatePassword = (passwordValue: string): boolean => {
        if (!passwordValue.trim()) {
            setPasswordError("Password is required");
            return false;
        }
        setPasswordError("");
        return true;
    };

    const handleLocalLogin = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validate inputs
        const isEmailValid = validateEmail(email);
        const isPasswordValid = validatePassword(password);

        if (!isEmailValid || !isPasswordValid) {
            return;
        }

        const result = await login({ email, password });
        if (result.success) {
            onClose();
        }
    };

    const handleProviderLogin = (provider: string) => {
        loginWithProvider(provider);
    };

    // Clear validation errors when modal closes
    useEffect(() => {
        if (!open) {
            setEmailError("");
            setPasswordError("");
            setEmail("");
            setPassword("");
        }
    }, [open]);

    const renderLocalLogin = () => {
        if (!authInfo?.local?.enabled) return null;

        return (
            <Box
                component="form"
                className="local-login-form"
                onSubmit={handleLocalLogin}
            >
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) {
                            validateEmail(e.target.value);
                        }
                    }}
                    onBlur={() => validateEmail(email)}
                    error={!!emailError}
                    helperText={emailError}
                    disabled={isLoading}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                        setPassword(e.target.value);
                        if (passwordError) {
                            validatePassword(e.target.value);
                        }
                    }}
                    onBlur={() => validatePassword(password)}
                    error={!!passwordError}
                    helperText={passwordError}
                    disabled={isLoading}
                />
                {error && (
                    <Typography color="error" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                )}
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    className="local-login-button"
                    disabled={isLoading}
                    sx={{ mt: 2, mb: 2 }}
                >
                    {isLoading ? "Logging in..." : "Login"}
                </Button>
                <Button
                    fullWidth
                    variant="text"
                    onClick={() => {
                        showForgotPassword(email);
                    }}
                    sx={{ mt: 1, textTransform: 'none' }}
                    className="forgot-password-link"
                >
                    Forgot password?
                </Button>
            </Box>
        );
    };
    const renderAzureLogin = () => {
        if (!authInfo?.azure?.enabled) return null;

        return (
            <Button
                fullWidth
                variant="contained"
                onClick={() => handleProviderLogin("azure")}
                startIcon={<WindowIcon />}
                className="azure-login-button"
                disabled={isLoading || loading === "azure"}
                sx={{ mt: 2, mb: 2 }}
            >
                {loading === "azure" ? "Redirecting..." : "Login with Azure AD"}
            </Button>
        );
    };
    const renderGoogleLogin = () => {
        if (!authInfo?.google?.enabled) return null;

        return (
            <Button
                fullWidth
                variant="outlined"
                onClick={() => handleProviderLogin("google")}
                startIcon={<GoogleIcon />}
                className="google-login-button"
                disabled={isLoading || loading === "google"}
                sx={{ mb: 2 }}
            >
                {loading === "google" ? "Redirecting..." : "Login with Google"}
            </Button>
        );
    };


    const hasLocalLogin = authInfo?.local?.enabled;
    const hasProviderLogin = authInfo?.azure?.enabled || authInfo?.google?.enabled;
    const showDivider = hasLocalLogin && hasProviderLogin;

    return (
        <Modal open={open} onClose={onClose}>
            <Box className="login-container">
                <Typography component="h1" variant="h5" sx={{ textAlign: 'center', mb: 3 }}>
                    Login
                </Typography>

                {renderLocalLogin()}

                {showDivider && (
                    <Box className="login-divider" sx={{ textAlign: 'center', my: 5 }}>
                        OR
                    </Box>
                )}

                {renderAzureLogin()}
                {renderGoogleLogin()}
            </Box>
        </Modal>
    );
}